package com.bikompanion.data.gbfs

import dev.zacsweers.metro.Inject
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlin.time.Duration
import kotlin.time.Duration.Companion.hours
import kotlin.time.TimeMark
import kotlin.time.TimeSource

private const val FEED_SYSTEM_INFORMATION = "system_information"
private const val FEED_STATION_INFORMATION = "station_information"
private const val FEED_STATION_STATUS = "station_status"
private const val FEED_VEHICLE_TYPES = "vehicle_types"

/**
 * How long the near-static feeds are trusted before being read again.
 *
 * Dock locations and the catalogue of bike models change on the scale of weeks. Refetching
 * them alongside every availability poll would multiply the bytes pulled off feeds we are
 * guests on, for data that comes back the same every time.
 */
private val StaticFeedLifetime = 12.hours

/**
 * Reads the GBFS feeds of any number of systems. Knows nothing about the domain.
 *
 * Everything except live availability is remembered per system, so a pan that brings a
 * network back into view doesn't re-download the ground it stands on.
 */
@Inject
class GbfsApi(
    private val httpClient: HttpClient,
) {
    private val discoveryLock = Mutex()
    private val feedUrls = mutableMapOf<String, Map<String, String>>()

    private val stationInformation = Memo<GbfsStationInformationResponse>()
    private val vehicleTypes = Memo<GbfsVehicleTypesResponse>()

    internal suspend fun systemInformation(system: GbfsSystem): GbfsSystemInformationResponse =
        httpClient.get(feedUrl(system, FEED_SYSTEM_INFORMATION)).body()

    /** Dock locations and names. Near-static, so kept for [StaticFeedLifetime]. */
    internal suspend fun stationInformation(system: GbfsSystem): GbfsStationInformationResponse =
        stationInformation.get(system.networkId) {
            httpClient.get(feedUrl(system, FEED_STATION_INFORMATION)).body()
        }

    /**
     * The catalogue of bike models.
     *
     * The only thing in the feed that says which of a station's bikes are electric — the
     * status rows carry opaque type ids and nothing else.
     */
    internal suspend fun vehicleTypes(system: GbfsSystem): GbfsVehicleTypesResponse =
        vehicleTypes.get(system.networkId) {
            httpClient.get(feedUrl(system, FEED_VEHICLE_TYPES)).body()
        }

    /** Live availability. Never remembered — this is the whole point of the refresh. */
    internal suspend fun stationStatus(system: GbfsSystem): GbfsStationStatusResponse =
        httpClient.get(feedUrl(system, FEED_STATION_STATUS)).body()

    /**
     * Where one of a system's feeds actually lives, from its discovery document.
     *
     * The document is read at most once per system and then kept. The lock is held only
     * around the map itself, never around the request — see [Memo] for why that matters.
     */
    private suspend fun feedUrl(
        system: GbfsSystem,
        name: String,
    ): String {
        val known = discoveryLock.withLock { feedUrls[system.networkId] }
            ?: run {
                val discovered: JsonObject = httpClient.get(system.discoveryUrl).body()
                val parsed = discovered.parseFeeds()
                // getOrPut, not put: another caller may have discovered the same system while
                // this request was in flight, and the two are interchangeable.
                discoveryLock.withLock { feedUrls.getOrPut(system.networkId) { parsed } }
            }
        return known[name]
            ?: error("GBFS feed '$name' is not published at ${system.discoveryUrl}")
    }
}

/**
 * Remembers one value per key for [StaticFeedLifetime].
 *
 * The lock is taken twice, briefly, and deliberately **not** held across [fetch]. Holding it
 * over the request deadlocked outright: the fetch needs the feed's URL, resolving that reads
 * the discovery document, and a coroutine [Mutex] is not reentrant — so the fetch waited on a
 * lock its own caller was holding, forever. That hung every station load behind a network
 * list served from cache, which showed up as a map with coverage drawn and no bikes on it.
 *
 * The cost is that two callers arriving together can both fetch. Harmless: it is the same
 * request twice, at most once per system per twelve hours, and whichever lands second is the
 * one kept.
 */
private class Memo<T> {
    private val lock = Mutex()
    private val entries = mutableMapOf<String, Cached<T>>()

    suspend fun get(
        key: String,
        fetch: suspend () -> T,
    ): T {
        lock.withLock { entries[key] }?.valueIfFresh()?.let { return it }
        val fresh = fetch()
        lock.withLock { entries[key] = Cached(fresh) }
        return fresh
    }

    private class Cached<T>(
        private val value: T,
        private val readAt: TimeMark = TimeSource.Monotonic.markNow(),
        private val lifetime: Duration = StaticFeedLifetime,
    ) {
        fun valueIfFresh(): T? = value.takeIf { readAt.elapsedNow() < lifetime }
    }
}

/**
 * The feeds a discovery document offers, by name.
 *
 * The document is shaped differently either side of GBFS 3.0. 3.0 puts one list at
 * `data.feeds`; 2.x keys `data` by language first, so the same system appears once per
 * translation with only the URLs differing. Parsed by hand rather than by two DTOs and a
 * guess at which one applies, since telling them apart is a single key lookup.
 */
private fun JsonObject.parseFeeds(): Map<String, String> {
    val data = this["data"]?.jsonObject ?: error("GBFS discovery document has no 'data'")
    val feeds = data["feeds"]?.jsonArray
        // GBFS 2.x. English if it is offered, otherwise whichever translation came first —
        // the feed URLs differ by language but the data behind them does not.
        ?: data.entries
            .sortedBy { it.key != GBFS_PREFERRED_LANGUAGE }
            .firstNotNullOfOrNull { (_, value) -> (value as? JsonObject)?.get("feeds")?.jsonArray }
        ?: error("GBFS discovery document lists no feeds")
    return feeds.toFeedMap()
}

private fun JsonArray.toFeedMap(): Map<String, String> =
    mapNotNull { entry ->
        val feed = entry as? JsonObject ?: return@mapNotNull null
        val name = feed["name"]?.jsonPrimitive?.content ?: return@mapNotNull null
        val url = feed["url"]?.jsonPrimitive?.content ?: return@mapNotNull null
        name to url
    }.toMap()
